/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useTextDimensions } from '../hooks/PRIVATE_useTextDimensions';
import { useCssVars } from '../hooks/PRIVATE_useCssVars/useCssVars';
import { styles } from './themes/ChartStyles.css';
import { useRef } from 'preact/hooks';
import { PlotArea } from '../PRIVATE_PlotArea';
import { useId } from '../hooks/UNSAFE_useId';
import { Axis } from '../PRIVATE_Axis/Axis';
import { useChartViewport } from '../hooks/PRIVATE_useChartViewport';
import {
  getAvailSpace,
  getLayoutInfo,
  getScrollBarSpace,
  updateScrollBarSpace
} from '../utils/PRIVATE_chartUtils/layoutUtils';
import { useChartNav } from '../hooks/PRIVATE_useChartNav/useChartNav';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { getAxesPosition } from '../PRIVATE_Axis/utils/axisUtils';
import { ScrollBar } from '../PRIVATE_ChartScrollBar';
import { useSelectDrill } from '../hooks/PRIVATE_useChartEvents/useSelectDrill';
import { useChartDatatipAndCursor } from '../hooks/PRIVATE_useChartEvents/useDatatipAndCursor';
import { getGroupNavUtil } from '../PRIVATE_Axis/utils/axisNavUtils';
import { ChartItemData, TimeAxisProps } from '../utils/UNSAFE_visTypes/chart';
import { Gridlines } from '../PRIVATE_PlotArea/Gridlines';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { getChartVars } from '../utils/PRIVATE_chartUtils';
import { useChartContextMenu } from '#hooks/PRIVATE_useChartContextMenu';
import { getElementData } from '#utils/UNSAFE_visUtils';
import { useTextTruncation } from '#PRIVATE_VisSVGText/useTextTruncation';
import { useChartMarquee } from '#hooks/PRIVATE_useChartMarquee';
import { ChartProps } from './Chart';
import type { gestureTypes } from '#hooks/PRIVATE_useVisContextMenu';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '#utils/UNSAFE_stringUtils';

type ChartWithDimensionsProps<K, D> = Omit<ChartProps<K, D>, 'width' | 'height'> & {
  width: number;
  height: number;
};

const chartVars = getChartVars();

export function ChartWithDimensions<K extends string | number, D extends ChartItemData<K>>({
  width,
  height,
  selectedIds,
  hiddenIds,
  hideAndShowBehavior = 'none',
  orientation = 'vertical',
  xAxis,
  yAxis,
  plotArea,
  dragMode = 'user',
  series,
  groups,
  isStacked = false,
  getDataItem,
  selectionMode = 'none',
  drilling = 'off',
  dataCursorPosition,
  onSelectionChange,
  isDataCursorEnabled,
  dataCursorStyle,
  onViewportChange,
  onDataCursorPositionChange,
  onItemDrill,
  onGroupDrill,
  onItemHover,
  onItemFocus,
  datatip,
  zoomAndScroll = 'off',
  offset,
  getNavUtil,
  getDataItemPos,
  getGapRatio,
  valueFormats,
  findNearest,
  getMarkerInfo,
  isRtl,
  children,
  testId,
  renderGridLinesInFront,
  contextMenuConfig,
  ...props
}: ChartWithDimensionsProps<K, D>) {
  const testIdProps = useTestId(testId);
  const rootRef = useRef<HTMLDivElement>(null);
  const isHoriz = orientation === 'horizontal';
  const { xAxisPosition, yAxisPosition } = getAxesPosition(yAxis, isHoriz, isRtl);
  const { resolvedVars, cssContent } = useCssVars(chartVars);
  const colors = Array.from(Array(12)).map((_, index) => {
    return resolvedVars[`color${index + 1}`];
  });
  const { textMeasureContent, getTextDimensions } = useTextDimensions();
  let availSpace = getAvailSpace(width, height);
  const updatedSpace = getScrollBarSpace(availSpace, zoomAndScroll, xAxisPosition);
  let scrollDims = updatedSpace.scrollDims;
  availSpace = updatedSpace.availSpace;
  const hiddenSet = new Set(hiddenIds);
  const { locale } = useUser();
  const {
    xProps,
    xScale,
    xViewportExtent,
    xAxisExtent,
    xIndexExtent,
    yViewportExtent,
    yAxisExtent,
    groupWidth,
    yProps,
    yScale,
    yMaxDim,
    yMajorTicks,
    yMinorTicks,
    plotAreaSpace,
    idToDataMap
  } = getLayoutInfo<K, D>(
    series,
    groups,
    getDataItem,
    xAxis,
    yAxis,
    availSpace,
    hiddenSet,
    hideAndShowBehavior,
    isHoriz,
    isRtl,
    isStacked,
    resolvedVars['labelFontSize'],
    offset,
    locale,
    getTextDimensions
  );

  scrollDims = updateScrollBarSpace(scrollDims, xAxisPosition === 'bottom', isRtl, yMaxDim);

  const gapRatio = getGapRatio(xIndexExtent.startIndex, xIndexExtent.endIndex);

  const getNextChartItem = getNavUtil(xIndexExtent.startIndex, xIndexExtent.endIndex);

  const renderedGroupsIndex = xProps.tickLabels.map(({ index }) => index + xIndexExtent.startIndex);
  const { getNextNavGroup, getDefaultNavGroup } = getGroupNavUtil(
    groups,
    renderedGroupsIndex,
    xIndexExtent,
    drilling,
    isRtl
  );

  const {
    cursor,
    marqueeProps,
    marqueeContent,
    toggleButtonContent,
    isPointInsideMarquee,
    dragMode: mode
  } = useChartMarquee<K, D>({
    plotAreaSpace,
    dragMode,
    chartWidth: width,
    isMultiSelection: selectionMode === 'multiple',
    onViewportChange,
    isZoomScroll: zoomAndScroll === 'live',
    xAxisExtent,
    xScale,
    selectedIds,
    getDataById: (id) => idToDataMap.get(id),
    onSelectionChange,
    viewportMax: xViewportExtent.viewportMax,
    viewportMin: xViewportExtent.viewportMin,
    isRtl
  });

  const { touchResponse, touchResponseStyle } = useVisTouchResponse({
    type: 'auto',
    supportsTouchDragGestures: mode !== 'off',
    rootRef
  });

  const { activeId, focusedItemInfo, hoveredItemInfo, focusedGroupInfo, navProps, onFocusUpdate } =
    useChartNav({
      rootRef,
      getNextChartItem,
      getNextNavGroup,
      getDefaultNavGroup,
      touchResponse,
      onItemHover,
      onItemFocus,
      drilling,
      xIndexExtent
    });

  const onContextMenuDismissed = (gesture: gestureTypes) => {
    if (gesture === 'keyboard') {
      onFocusUpdate();
    }
  };

  const getTextAndBounds = (target: EventTarget | null) => {
    if (target != rootRef.current) {
      const dataSet = getElementData(rootRef, target as HTMLElement);
      const text = dataSet?.['ojText']!;

      if (!text) {
        return;
      }

      if (text === xAxis?.title) {
        return {
          text,
          bounds: xProps.titleProps?.dims
        };
      } else if (text === yAxis?.title) {
        return {
          text,
          bounds: yProps.titleProps?.dims
        };
      } else if (dataSet?.['ojGroupIndex']) {
        const index = parseInt(dataSet['ojGroupIndex']);
        return {
          bounds: xProps.tickLabels[index].dims,
          text: text!
        };
      }
    }

    if (target === rootRef.current && focusedGroupInfo.isFocusVisible) {
      const group = groups[focusedGroupInfo.groupIndex];
      return {
        bounds: xProps.tickLabels[focusedGroupInfo.groupIndex].dims,
        text: group.name || group.id
      };
    }
    return;
  };

  const { tooltipContent, tooltipProps } = useTextTruncation({
    getTextAndBounds,
    width,
    isRtl
  });

  const { selectDrillProps, selectionContent } = useSelectDrill<K, D>({
    selectionMode,
    selection: [...selectedIds],
    onChange: onSelectionChange,
    rootRef,
    drilling,
    onItemDrill,
    onGroupDrill,
    focusedGroupInfo,
    focusedItemInfo,
    groups,
    getDataItem,
    getDataById: (id) => idToDataMap.get(id)
  });

  const viewportProps = useChartViewport({
    rootRef,
    width,
    height,
    xViewportExtent,
    yViewportExtent,
    xAxisExtent,
    yAxisExtent,
    xScale,
    yScale,
    plotAreaSpace,
    isRtl,
    isHoriz,
    zoomAndScroll,
    onViewportChange
  });

  const findNearestData = findNearest(xScale as any, yScale, groupWidth, gapRatio);
  const getDataItemPosition = getDataItemPos(xScale as any, yScale, groupWidth, gapRatio);

  const {
    datatipContent,
    dataCursorContent,
    datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
  } = useChartDatatipAndCursor<K, D>({
    series,
    groups,
    width,
    height,
    findNearest: findNearestData,
    plotAreaSpace,
    onDataCursorPositionChange,
    isDataCursorEnabled,
    dataCursorPosition,
    dataCursorStyle,
    hoveredItemInfo,
    focusedItemInfo,
    getDataItem,
    getMarkerInfo,
    getDataItemPos: getDataItemPosition,
    datatip,
    valueFormats,
    isHoriz,
    touchResponse,
    isDisabled: mode != 'off',
    xScale: xScale as any,
    yScale,
    timeAxisType: (xAxis as TimeAxisProps)?.timeAxisType
  });

  const isGroupInteractive = (index: number) => {
    if ((xAxis as TimeAxisProps)?.timeAxisType != null) {
      return false;
    }
    const groupDrill = groups[index].drilling;
    if (groupDrill === 'on') {
      return true;
    }

    if (groupDrill != 'off' && (drilling === 'on' || drilling === 'groupsOnly')) {
      return true;
    }
    return false;
  };

  const plotAreaClipPathId = useId();
  const { viewportMin: xViewportMin, viewportMax: xViewportMax } = xViewportExtent;
  const { startIndex: xStartIndex, endIndex: xEndIndex } = xIndexExtent;
  const gridLines = (
    <Gridlines
      plotArea={plotArea}
      yMinorTicks={yMinorTicks}
      xScale={xScale}
      yScale={yScale}
      isLog={yAxis?.scale === 'log'}
      xViewportMin={xViewportMin}
      xViewportMax={xViewportMax}
      yTicks={yMajorTicks}
      yAxisPosition={yAxisPosition}
    />
  );
  const { contextMenuContent, contextMenuProps } = useChartContextMenu<K, D>({
    series,
    groups,
    getDataItem,
    focusedItemInfo,
    rootRef,
    xAxisTitle: xAxis?.title,
    yAxisTitle: yAxis?.title,
    contextMenuConfig,
    onContextMenuDismissed
  });

  const eventsProps = mergeProps(
    navProps,
    selectDrillProps,
    viewportProps,
    datatipProps,
    marqueeProps,
    tooltipProps,
    contextMenuProps
  );

  return (
    <div
      tabIndex={0}
      ref={rootRef}
      aria-activedescendant={activeId}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      aria-describedby={merge([props['aria-describedby'], datatipAriaDescribedby])}
      style={{ width, height, ...touchResponseStyle }}
      role="application"
      className={styles.outline}
      {...testIdProps}
      {...eventsProps}>
      <svg width="100%" height="100%">
        <clipPath id={`${plotAreaClipPathId}`}>
          <rect {...plotAreaSpace}></rect>
        </clipPath>
        <PlotArea
          color={plotArea?.color}
          yScale={yScale}
          cursor={cursor}
          isLog={yAxis?.scale === 'log'}
          availSpace={plotAreaSpace}
          xAxisPosition={xAxisPosition}
        />
        {!renderGridLinesInFront && gridLines}
        <Axis {...(yProps as any)} axisPosition={yAxisPosition} isRtl={isRtl} />
        <Axis
          {...xProps}
          isRtl={isRtl}
          axisPosition={xAxisPosition}
          isTextInteractive={isGroupInteractive}
          activeId={activeId}
          focusedGroupIndex={
            focusedGroupInfo.isFocusVisible ? focusedGroupInfo.groupIndex : undefined
          }
        />
        <g clip-path={`url(#${plotAreaClipPathId})`}>
          {children({
            xStartIndex,
            xEndIndex,
            colors,
            xScale: xScale as any,
            yScale,
            isPointInsideMarquee,
            defaultFontSize: resolvedVars['labelFontSize'],
            gapRatio,
            activeId,
            focusedItemInfo,
            hoveredItemInfo,
            getTextDimensions,
            groupWidth
          })}
          {renderGridLinesInFront && gridLines}
          {dataCursorContent}
        </g>
        {zoomAndScroll === 'live' && (
          <ScrollBar
            availSpace={scrollDims}
            isRtl={isRtl}
            viewport={xViewportExtent}
            axisExtent={xAxisExtent}
            isHoriz={xAxisPosition === 'bottom'}
          />
        )}
        {marqueeContent}
      </svg>
      {toggleButtonContent}
      {textMeasureContent}
      {cssContent}
      {datatipContent}
      {tooltipContent}
      {contextMenuContent}
      {selectionContent}
    </div>
  );
}
